import jwtDecode from "jwt-decode";

/**
 * Auth0 custom claim key where the user's upstream identity provider access
 * token is embedded in the Superblocks-issued Auth0 JWT.
 */
export const IDP_TOKEN_KEY = "https://superblocks/api/idp_token";

/**
 * Superblocks JWT claim holding the same provider token, except the server
 * re-mints this one out of the Token Vault on every mint. The Auth0 claim above
 * is only ever the token from the original login, so it goes stale within the
 * hour and nothing the user does short of logging in again moves it.
 */
export const FEDERATED_TOKEN_KEY = "federated_token";
export const FEDERATED_TOKEN_EXPIRY_SKEW_MS = 60_000;

export function extractJwtStringClaim(
  jwt: string | null | undefined,
  claimKey: string,
): string | undefined {
  if (!jwt) return undefined;
  try {
    const decoded = jwtDecode<Record<string, unknown>>(jwt);
    const claim = decoded[claimKey];
    return typeof claim === "string" && claim.length > 0 ? claim : undefined;
  } catch {
    return undefined;
  }
}

export function getJwtClaimExpirationMs(
  outerJwt: string | null | undefined,
  claimKey: string,
): number | null {
  const nestedJwt = extractJwtStringClaim(outerJwt, claimKey);
  if (!nestedJwt) return null;
  try {
    const decoded = jwtDecode<Record<string, unknown>>(nestedJwt);
    return typeof decoded.exp === "number" && Number.isFinite(decoded.exp)
      ? decoded.exp * 1000
      : null;
  } catch {
    return null;
  }
}

export function isJwtClaimExpiring(
  outerJwt: string | null | undefined,
  claimKey: string,
  staleAtMs: number,
): boolean {
  const expirationMs = getJwtClaimExpirationMs(outerJwt, claimKey);
  return expirationMs !== null && expirationMs <= staleAtMs;
}

/**
 * Extract the raw IdP token string from a Superblocks Auth0 JWT, or
 * `undefined` if the outer JWT cannot be decoded or the claim is missing.
 */
export function extractIdpToken(auth0Jwt: string): string | undefined {
  return extractJwtStringClaim(auth0Jwt, IDP_TOKEN_KEY);
}

/**
 * Returns true only when two Superblocks session JWTs carry different IdP
 * authorizations and the current one is usable. Session JWT rotation by itself
 * is not a reason to invalidate an execution-scoped token, and neither is the
 * claim disappearing: a re-mint copies the claim from the current session, so
 * when that has none there is nothing fresher to mint.
 *
 * Compare session JWTs with each other only. A scoped token minted for an
 * organization with Token Vault refresh can carry a newer provider token than
 * the session it was minted from, so it legitimately differs from the session.
 */
export function hasFederatedAuthorizationChanged(
  previousJwt: string | null | undefined,
  currentJwt: string | null | undefined,
): boolean {
  if (!previousJwt || !currentJwt) return false;
  const currentFederatedToken = extractJwtStringClaim(
    currentJwt,
    FEDERATED_TOKEN_KEY,
  );
  if (!currentFederatedToken) return false;
  return (
    extractJwtStringClaim(previousJwt, FEDERATED_TOKEN_KEY) !==
    currentFederatedToken
  );
}

/**
 * Result of inspecting the IdP token the browser holds for the current user.
 *
 * The shape matches the contract we enforce end-to-end: the UI should only
 * block locally on things it can actually observe (no token, or a JWT that is
 * definitely expired). Opaque tokens (e.g. Google `ya29.*` access tokens) are
 * forwarded to the server and validated there when the relevant feature flag
 * is enabled on the orchestrator
 * (`agent.plugins.auth.validate_subject_token_during_obo_flow.enabled`).
 */
export type IdpTokenInspection =
  | { present: false }
  | { present: true; format: "opaque" }
  | { present: true; format: "jwt"; expired: boolean };

/**
 * Inspect the provider token carried in one claim of an outer JWT. Pure function
 * — accepts the outer JWT string and returns a structured description of the
 * token it carries without ever throwing.
 */
export function inspectIdpTokenInJwt(
  outerJwt: string | null | undefined,
  claimKey: string,
): IdpTokenInspection {
  if (!outerJwt) {
    return { present: false };
  }

  let decodedOuterJwt: Record<string, unknown>;
  try {
    decodedOuterJwt = jwtDecode(outerJwt);
  } catch {
    return { present: false };
  }

  const idpToken = decodedOuterJwt[claimKey];
  if (typeof idpToken !== "string" || idpToken.length === 0) {
    return { present: false };
  }

  let decodedIdpToken: Record<string, unknown>;
  try {
    decodedIdpToken = jwtDecode(idpToken);
  } catch {
    // Not a JWT (e.g. a Google `ya29.*` opaque access token). We cannot
    // inspect its expiry locally; forward to the server for validation.
    return { present: true, format: "opaque" };
  }

  const exp = decodedIdpToken.exp;
  // A JWT without `exp` is treated as non-expired locally; any expiry
  // enforcement is the server's responsibility.
  const expired = typeof exp === "number" ? exp < Date.now() / 1000 : false;
  return { present: true, format: "jwt", expired };
}
